import fs from 'fs/promises'
import path from 'path'
import { BaseStorageProvider } from './base.provider.js'
import type { FileToUpload, UploadedFile, DownloadedFile, LocalConfig } from '../types.js'
import { getFileSize, getTimestamp, sanitizePath, ensureDir } from '../utils.js'

/**
 * Local filesystem storage provider
 */
export class LocalStorageProvider extends BaseStorageProvider {
  readonly name = 'local' as const
  private config: Required<Omit<LocalConfig, 'basePath'>> & Pick<LocalConfig, 'basePath'>

  constructor(config: LocalConfig, verbose: boolean = false) {
    super(verbose)
    this.config = {
      directory: config.directory,
      createIfNotExists: config.createIfNotExists ?? true
    }
  }

  async initialize(): Promise<void> {
    try {
      if (this.config.createIfNotExists) {
        await ensureDir(this.config.directory)
      } else {
        // Verify directory exists
        await fs.access(this.config.directory)
      }

      this.log(`Initialized local storage: ${this.config.directory}`)
    } catch (error) {
      this.logError('Failed to initialize local storage', error as Error)
      throw error
    }
  }

  async uploadFile(file: FileToUpload): Promise<UploadedFile> {
    const localPath = file.path
    const destination = file.destination || path.basename(localPath)
    const remotePath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(destination))
      : sanitizePath(destination)
    const fullPath = path.join(this.config.directory, remotePath)

    const size = await getFileSize(localPath)

    try {
      // Ensure subdirectories exist
      await ensureDir(path.dirname(fullPath))

      // Copy file
      await fs.copyFile(localPath, fullPath)

      this.log(`Copied ${localPath} → ${fullPath}`)

      return {
        localPath,
        remotePath,
        url: `file://${fullPath}`,
        size,
        contentType: file.contentType,
        uploadedAt: getTimestamp()
      }
    } catch (error) {
      this.logError(`Failed to copy ${localPath}`, error as Error)
      throw error
    }
  }

  async fileExists(remotePath: string): Promise<boolean> {
    const fullPath = this.config.basePath
      ? path.join(this.config.directory, this.config.basePath, sanitizePath(remotePath))
      : path.join(this.config.directory, sanitizePath(remotePath))

    try {
      await fs.access(fullPath)
      return true
    } catch {
      return false
    }
  }

  async downloadFile(remotePath: string): Promise<DownloadedFile> {
    const fullPath = this.config.basePath
      ? path.join(this.config.directory, this.config.basePath, sanitizePath(remotePath))
      : path.join(this.config.directory, sanitizePath(remotePath))

    try {
      const buffer = await fs.readFile(fullPath)
      const stats = await fs.stat(fullPath)

      this.log(`Downloaded ${remotePath} (${buffer.length} bytes)`)

      return {
        remotePath,
        localPath: fullPath,
        buffer,
        url: `file://${fullPath}`,
        size: stats.size,
        downloadedAt: getTimestamp()
      }
    } catch (error) {
      this.logError(`Failed to download ${remotePath}`, error as Error)
      throw error
    }
  }

  async deleteFile(remotePath: string): Promise<void> {
    const fullPath = this.config.basePath
      ? path.join(this.config.directory, this.config.basePath, sanitizePath(remotePath))
      : path.join(this.config.directory, sanitizePath(remotePath))

    try {
      await fs.unlink(fullPath)
      this.log(`Deleted ${remotePath}`)
    } catch (error) {
      this.logError(`Failed to delete ${remotePath}`, error as Error)
      throw error
    }
  }

  async listFiles(remotePath?: string): Promise<string[]> {
    const fullPath = remotePath
      ? path.join(this.config.directory, sanitizePath(remotePath))
      : this.config.directory

    try {
      const files: string[] = []

      async function walk(dir: string, baseDir: string): Promise<void> {
        const entries = await fs.readdir(dir, { withFileTypes: true })

        for (const entry of entries) {
          const fullEntryPath = path.join(dir, entry.name)
          const relativePath = path.relative(baseDir, fullEntryPath)

          if (entry.isDirectory()) {
            await walk(fullEntryPath, baseDir)
          } else {
            files.push(relativePath)
          }
        }
      }

      await walk(fullPath, this.config.directory)
      return files
    } catch (error) {
      this.logError('Failed to list files', error as Error)
      throw error
    }
  }

  async cleanup(): Promise<void> {
    this.log('Local storage cleanup (no-op)')
  }

  /**
   * Get the full local path for a remote path
   */
  getFullPath(remotePath: string): string {
    return path.join(this.config.directory, sanitizePath(remotePath))
  }
}
